import { PlDialog } from "./pl--dialog.js"

/**
 * PlFilterDrawer - Custom element for filter drawer
 * Extends PlDialog and handles filter drawer behavior
 */
class PlFilterDrawer extends PlDialog {
  connectedCallback() {
    super.connectedCallback()
    this.#attachFilterListeners()

    // Setup accordion behavior when drawer opens
    this.addEventListener("pl-dialog:opened", () => {
      setTimeout(() => {
        this.#setupAccordionBehavior()
        this.#setupFormSubmission()
      }, 100) // Wait for dropdowns to initialize
    })
  }

  #attachFilterListeners() {
    // Listen for manual open requests
    document.addEventListener("filter:open", (event) => {
      const drawerId = event.detail?.drawerId
      if (!drawerId || this.dataset.dialogId === drawerId) {
        this.open()
      }
    })

    // Auto-bind all [data-filter-open] buttons
    document.querySelectorAll("[data-filter-open]").forEach((button) => {
      const targetDrawerId = button.dataset.filterOpen
      if (targetDrawerId === this.dataset.dialogId) {
        button.addEventListener("click", () => this.open())
      }
    })

    // Handle close button clicks inside drawer
    const closeButtons = this.querySelectorAll("[data-filter-drawer-close]")
    closeButtons.forEach((btn) => {
      btn.addEventListener("click", () => this.close())
    })
  }

  #setupAccordionBehavior() {
    const drawerContent = this.querySelector(".pl--filter-drawer__content")
    if (!drawerContent) return

    const dropdowns = drawerContent.querySelectorAll("pl-dropdown")

    dropdowns.forEach((dropdown) => {
      // Prevent popover from closing on outside click in drawer
      dropdown.dataset.closeOnOutsideClick = "false"

      const trigger = dropdown.querySelector("button")
      const content = dropdown.querySelector("[data-pl-popover]")

      if (!trigger || !content) return

      // Function to set max-height based on actual content height
      const setMaxHeight = () => {
        if (content.getAttribute("aria-hidden") === "false") {
          // Temporarily set to auto to measure
          content.style.maxHeight = "none"
          const height = content.scrollHeight
          content.style.maxHeight = `${height}px`
        } else {
          content.style.maxHeight = "0"
        }
      }

      // Override the popover's handleTriggerClick method
      if (!dropdown._originalHandleTriggerClick) {
        // Store original method
        dropdown._originalHandleTriggerClick = dropdown.handleTriggerClick?.bind(dropdown)

        // Replace with our accordion behavior
        dropdown.handleTriggerClick = (e) => {
          e.preventDefault()
          e.stopPropagation()

          const isCurrentlyOpen = dropdown.getAttribute("aria-expanded") === "true"

          if (isCurrentlyOpen) {
            // Get current height before closing
            const currentHeight = content.scrollHeight
            content.style.maxHeight = `${currentHeight}px`

            // Force reflow
            void content.offsetHeight

            // Close this dropdown
            dropdown.close()
            // Set to 0 immediately (no requestAnimationFrame delay)
            content.style.maxHeight = "0"
          } else {
            // Close all other dropdowns first
            dropdowns.forEach((otherDropdown) => {
              if (
                otherDropdown !== dropdown &&
                otherDropdown.getAttribute("aria-expanded") === "true"
              ) {
                const otherContent = otherDropdown.querySelector("[data-pl-popover]")
                if (otherContent) {
                  const otherHeight = otherContent.scrollHeight
                  otherContent.style.maxHeight = `${otherHeight}px`
                  void otherContent.offsetHeight // Force reflow
                  otherDropdown.close()
                  otherContent.style.maxHeight = "0"
                }
              }
            })

            // Open this dropdown
            dropdown.open()
            // Set max-height immediately after open
            requestAnimationFrame(() => {
              content.style.maxHeight = "none"
              const height = content.scrollHeight
              content.style.maxHeight = `${height}px`
            })
          }
        }

        // Listen for popover state changes to update height
        dropdown.addEventListener("pl-popover:opened", () => {
          requestAnimationFrame(() => {
            setMaxHeight()
          })
        })

        dropdown.addEventListener("pl-popover:closed", () => {
          requestAnimationFrame(() => {
            content.style.maxHeight = "0"
          })
        })

        // Initial height setup
        setMaxHeight()

        // Update height when content changes (e.g., filters applied)
        const observer = new MutationObserver(() => {
          if (content.getAttribute("aria-hidden") === "false") {
            requestAnimationFrame(() => {
              setMaxHeight()
            })
          }
        })
        observer.observe(content, {
          childList: true,
          subtree: true,
          attributes: false,
        })
      }
    })
  }

  #setupFormSubmission() {
    const drawerForm = this.querySelector("#FilterForm")
    if (!drawerForm) return

    // Update all form attributes to point to drawer form
    const formInputs = drawerForm.querySelectorAll("input[form], button[form], select[form]")
    formInputs.forEach((input) => {
      const currentForm = input.getAttribute("form")
      if (currentForm && currentForm !== "FilterForm") {
        input.setAttribute("form", "FilterForm")
      }
    })
  }
}

if (!customElements.get("pl-filter-drawer")) {
  customElements.define("pl-filter-drawer", PlFilterDrawer)
}

export { PlFilterDrawer }
